import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where } from 'firebase/firestore';
import { db, initAnalytics, watchAuthState } from './firebase';
import { crearWorkspace, unirseAWorkspace } from './api';
import { subscribeWithRetry } from './firestoreRetry';
import { translate, getStoredLanguage, setStoredLanguage } from './i18n';
import { getStoredTheme, useTheme } from './theme';
import LanguageToggle from './LanguageToggle';
import './SubscribePage.css';
import './WorkspacesPage.css';

export default function WorkspacesPage() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => getStoredLanguage());
  const t = (key) => translate(language, key);
  useTheme(getStoredTheme());
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setStoredLanguage(lang);
  };

  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [newName, setNewName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const unsubscribeAuth = watchAuthState((nextUser) => {
      setUser(nextUser);
      // A direct/shared link to this page hit while logged out (or before
      // Firebase has restored the session) - bounce to the login screen
      // rather than showing forms that would just fail with 401s.
      if (!nextUser) navigate('/');
    });
    return unsubscribeAuth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live list, not a one-shot load: reflects a workspace someone just
  // invited you into, or renamed/removed, without needing a manual refresh.
  useEffect(() => {
    if (!user?.uid) {
      setWorkspaces([]);
      return;
    }
    const workspacesQuery = query(collection(db, 'workspaces'), where('memberUids', 'array-contains', user.uid));
    // See src/firestoreRetry.js - a listener can still race Firebase Auth's
    // token attachment right as a session is restored, even gated on
    // user?.uid like this already is.
    return subscribeWithRetry(
      () => workspacesQuery,
      (snapshot) => {
        const list = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        setWorkspaces(list);
      },
      () => {}
    );
  }, [user?.uid]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError('');
    try {
      const workspace = await crearWorkspace({ name: newName.trim() });
      setNewName('');
      navigate(`/workspaces/${workspace.id}`);
    } catch {
      setError(t('errCreateWorkspace'));
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || joining) return;
    setJoining(true);
    setError('');
    try {
      const result = await unirseAWorkspace({ code: joinCode.trim() });
      setJoinCode('');
      navigate(`/workspaces/${result.workspaceId}`);
    } catch {
      setError(t('errJoinWorkspace'));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="sub-page">
      <header className="sub-page-header">
        <div className="sub-page-titlebar">
          <div className="sub-page-titlebar-start">
            <button
              className="sub-back-icon-btn"
              type="button"
              onClick={() => navigate('/')}
              aria-label={t('backAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="sub-page-title">{t('workspacesTitle')}</h1>
          </div>
          <LanguageToggle language={language} onChange={handleLanguageChange} />
        </div>
      </header>

      <main className="sub-page-main workspaces-main">
        <div className="workspaces-panel">
          {error && <p className="workspace-error">{error}</p>}

          <div className="workspace-list">
            {workspaces.length === 0 && (
              <p className="workspace-empty">{t('noWorkspaces')}</p>
            )}
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                type="button"
                className="workspace-list-item"
                onClick={() => navigate(`/workspaces/${workspace.id}`)}
              >
                <span className="workspace-list-item-name">{workspace.name}</span>
                <span className={`workspace-mode-badge workspace-mode-${workspace.mode}`}>
                  {workspace.mode === 'work' ? t('workModeLabel') : t('chatModeLabel')}
                </span>
              </button>
            ))}
          </div>

          <form className="workspace-form" onSubmit={handleCreate}>
            <input
              className="workspace-input"
              type="text"
              placeholder={t('workspaceNamePlaceholder')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
              disabled={creating}
            />
            <button className="workspace-submit-btn" type="submit" disabled={creating || !newName.trim()}>
              {t('createWorkspace')}
            </button>
          </form>

          <form className="workspace-form" onSubmit={handleJoin}>
            <input
              className="workspace-input"
              type="text"
              placeholder={t('joinCodePlaceholder')}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={16}
              disabled={joining}
            />
            <button className="workspace-submit-btn secondary" type="submit" disabled={joining || !joinCode.trim()}>
              {t('join')}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
